import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Award, CalendarPlus, Pencil, QrCode, Users } from 'lucide-react';
import { isSingleEventActivity, type ActivitySession } from 'shared';
import {
  useActivity,
  useActivitySession,
  useActivitySessions,
  type Activity,
} from '@/hooks/useActivities';
import ActivityModal from '@/components/activities/ActivityModal';
import SessionBoard from '@/components/activities/SessionBoard';
import SessionCreateModal from '@/components/activities/SessionCreateModal';
import {
  ErrorBlock,
  LoadingBlock,
  TypeChip,
  UniversoChip,
} from '@/components/activities/ActivityUi';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';
import {
  formatSessionDate,
  formatSessionSpan,
  nextSessionId,
  sessionPhase,
  type SessionPhase,
} from '@/lib/activityTime';

const PHASE_LABEL: Record<SessionPhase, string> = {
  open: 'A decorrer',
  upcoming: 'Agendada',
  over: 'Terminada',
};

const PHASE_STYLE: Record<SessionPhase, string> = {
  open: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  over: 'bg-muted text-muted-foreground',
};

/** Everyone counted at a session: registered participants plus visitors. */
function headcount(session: ActivitySession): number {
  return session.attendanceCount + (session.visitorHeadcount ?? 0);
}

function InfoBlock({ title, text }: { title: string; text: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">
        {title}
      </p>
      <p className="text-sm text-foreground whitespace-pre-line">{text}</p>
    </div>
  );
}

/** What the activity is, for whoever opens it: description, syllabus, tools. */
function ActivityInfo({ activity }: { activity: Activity }) {
  if (!activity.description && !activity.syllabus && !activity.tools) return null;
  return (
    <div className="card p-6 grid gap-6 md:grid-cols-3">
      {activity.description && <InfoBlock title="Descrição" text={activity.description} />}
      {activity.syllabus && <InfoBlock title="Programa" text={activity.syllabus} />}
      {activity.tools && <InfoBlock title="Ferramentas" text={activity.tools} />}
    </div>
  );
}

type ModalState = 'none' | 'edit' | 'session';

/**
 * An activity and its sessions. An open day is one event, so there is no list
 * between it and its code: its page is the code, the roster and the visitors.
 */
export default function AtividadeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toasts, toast, dismiss } = useToast();

  const activityId = Number(id);
  const validId = Number.isInteger(activityId) && activityId > 0;

  const activityQuery = useActivity(validId ? activityId : null);
  const sessionsQuery = useActivitySessions(validId ? activityId : null);
  const [modal, setModal] = useState<ModalState>('none');

  const activity = activityQuery.data;
  const sessions = sessionsQuery.data?.sessions ?? [];

  // An open day has one session, which is the open day. One from before that
  // was so may have several, and is then listed like any other activity.
  const openDay = activity !== undefined && isSingleEventActivity(activity.type);
  const asEvent = openDay && (sessionsQuery.isLoading || sessions.length === 1);
  const eventSessionId = asEvent && sessions.length === 1 ? sessions[0].id : null;
  const eventQuery = useActivitySession(eventSessionId);
  const event = eventSessionId !== null ? eventQuery.data : undefined;

  const editButton = (
    <button
      type="button"
      onClick={() => setModal('edit')}
      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
    >
      <Pencil className="w-4 h-4" />
      Editar
    </button>
  );

  // Every session in date order, past ones included. The one on now, or else
  // the next to come, is highlighted and scrolled to, with the one before it
  // still in view.
  const now = Date.now();
  const nextId = nextSessionId(sessions, now);
  const listRef = useRef<HTMLDivElement>(null);
  const nextRowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const row = nextRowRef.current;
    if (!list || !row) return;
    const previous = row.previousElementSibling as HTMLElement | null;
    const header = list.querySelector('thead') as HTMLElement | null;
    const target = (previous ?? row).offsetTop - (header?.offsetHeight ?? 0);
    list.scrollTop = Math.max(0, target);
  }, [nextId]);

  return (
    <>
      <Helmet>
        <title>{activity ? `${activity.name} | DIGGER` : 'Atividade | DIGGER'}</title>
      </Helmet>

      <div className="space-y-6">
        <Link
          to="/atividades"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Atividades
        </Link>

        {!validId && <ErrorBlock message="Atividade inválida." />}
        {validId && activityQuery.isLoading && <LoadingBlock />}
        {validId && activityQuery.isError && (
          <ErrorBlock message={activityQuery.error?.message ?? 'Erro ao carregar a atividade.'} />
        )}

        {asEvent && (sessionsQuery.isLoading || eventQuery.isLoading) && <LoadingBlock />}
        {asEvent && eventQuery.isError && (
          <ErrorBlock message={eventQuery.error?.message ?? 'Erro ao carregar o Dia Aberto.'} />
        )}
        {activity && event && (
          <SessionBoard
            session={event}
            actions={editButton}
            onCancelled={() => navigate('/atividades', { replace: true })}
            onSuccess={(msg) => toast.success(msg)}
            onError={(msg) => toast.error(msg)}
          >
            <ActivityInfo activity={activity} />
          </SessionBoard>
        )}

        {activity && !asEvent && (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-foreground">{activity.name}</h1>
                  <TypeChip type={activity.type} />
                  {activity.universo && <UniversoChip universo={activity.universo} />}
                </div>
                <p className="text-muted-foreground mt-1">
                  {activity.digitalCenterName} · Responsável: {activity.ownerName}
                </p>
                {activity.badgeName && (
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Award className="w-4 h-4" />
                    Atribui a credencial {activity.badgeName}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {editButton}
                {!openDay && (
                  <button
                    type="button"
                    onClick={() => setModal('session')}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    Nova Sessão
                  </button>
                )}
              </div>
            </div>

            <ActivityInfo activity={activity} />

            <section aria-labelledby="sessions-heading" className="space-y-3">
              <h2 id="sessions-heading" className="text-lg font-semibold text-foreground">
                {openDay ? 'Data' : 'Sessões'}
              </h2>

              <div ref={listRef} className="card overflow-auto max-h-[32rem]">
                {sessionsQuery.isLoading && <LoadingBlock />}
                {sessionsQuery.isError && (
                  <ErrorBlock
                    message={sessionsQuery.error?.message ?? 'Erro ao carregar as sessões.'}
                  />
                )}

                {!sessionsQuery.isLoading && !sessionsQuery.isError && sessions.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 gap-4">
                    <p className="text-base font-medium text-muted-foreground">
                      {openDay
                        ? 'Este Dia Aberto não tem data marcada.'
                        : 'Esta atividade ainda não tem sessões.'}
                    </p>
                    <button
                      type="button"
                      onClick={() => setModal('session')}
                      className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      <CalendarPlus className="w-4 h-4" />
                      {openDay ? 'Marcar data' : 'Criar a primeira sessão'}
                    </button>
                  </div>
                )}

                {sessions.length > 0 && (
                  <div className="overflow-x-auto">
                  <table
                    className="w-full min-w-[640px] text-sm"
                    aria-label={openDay ? 'Datas do Dia Aberto' : 'Sessões da atividade'}
                  >
                    <thead className="sticky top-0 z-10 bg-card">
                      <tr className="border-b border-border bg-muted/30">
                        {['Data', 'Horário', 'Dinamizador', 'Estado', 'Presentes'].map((h) => (
                          <th
                            key={h}
                            scope="col"
                            className="px-4 py-3 text-left font-medium text-muted-foreground"
                          >
                            {h}
                          </th>
                        ))}
                        <th scope="col" className="px-4 py-3 w-32">
                          <span className="sr-only">Abrir</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {sessions.map((s) => {
                        const phase = sessionPhase(s.startsAt, s.endsAt, now);
                        const isNext = s.id === nextId;
                        return (
                          <tr
                            key={s.id}
                            ref={isNext ? nextRowRef : undefined}
                            aria-current={isNext ? 'true' : undefined}
                            className={`transition-colors cursor-pointer ${
                              isNext
                                ? 'bg-primary/10 hover:bg-primary/15 shadow-[inset_4px_0_0_hsl(var(--primary))]'
                                : 'hover:bg-accent/30'
                            }`}
                            onClick={() => navigate(`/atividades/sessoes/${s.id}`)}
                          >
                            <td className="px-4 py-3 font-medium text-foreground tabular-nums">
                              <span className="inline-flex items-center gap-2">
                                {formatSessionDate(s.startsAt)}
                                {isNext && phase === 'upcoming' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                                    Próxima
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground tabular-nums">
                              {formatSessionSpan(s.startsAt, s.endsAt)}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{s.leaderName}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PHASE_STYLE[phase]}`}
                              >
                                {PHASE_LABEL[phase]}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground tabular-nums">
                              <span className="inline-flex items-center gap-1.5">
                                <Users className="w-4 h-4" />
                                {headcount(s)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Link
                                to={`/atividades/sessoes/${s.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80"
                                aria-label={`Abrir a sessão de ${formatSessionDate(s.startsAt)}`}
                              >
                                <QrCode className="w-4 h-4" />
                                Abrir
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {activity && modal === 'edit' && (
        <ActivityModal
          mode="edit"
          initial={activity}
          onClose={() => setModal('none')}
          onSuccess={(msg) => toast.success(msg)}
          onError={(msg) => toast.error(msg)}
        />
      )}
      {activity && modal === 'session' && (
        <SessionCreateModal
          activity={activity}
          onClose={() => setModal('none')}
          onSuccess={(msg) => toast.success(msg)}
          onError={(msg) => toast.error(msg)}
        />
      )}

      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
